import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MapComponent from "../../Dashboard/MapComponent";
import { env } from '../../../env';
import { AuthContext } from '../../../context';
import Swal from 'sweetalert2';

const CustomerFormModal = ({ customerId, title, type, setShowCustomerFormModal, sendInfoFromChild }) => {
    const { t } = useTranslation();
    const authContext = useContext(AuthContext);
    const baseUrl = env.REACT_APP_GOOGLE_API_URL;
    const apiKey = authContext.userInfo?.companyInfo?.mapsAPIKey;
    const [showMap, setShowMap] = useState(false);
    const [formButton, setFormButton] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [inputs, setInputs] = useState({
        contactName: '',
        postalCode: '',
        address: '',
        country: '',
        state: '',
        city: '',
        longitude: '',
        latitude: '',
        mobileNumber: '',
        phoneNumber: '',
        email: '',
        vat: '',
        comments: '',
        location: '',
        countryCode: "",
    });
    const [suggestions, setSuggestions] = useState([]);
    const [autocomplete, setAutocomplete] = useState(null);
    const formatVatLabel = (text) => {
        const parts = text.split(' ');
        if (parts.length === 2 && parts[0].toLowerCase() === 'vat') {
            return 'VAT ' + parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
        }
        return text.charAt(0).toUpperCase() + text.slice(1); // fallback
    };


    useEffect(() => {
        const loadGoogleMapsScript = () => {
            const script = document.createElement('script');
            script.src = `${baseUrl}/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.onload = initAutocomplete;
            document.body.appendChild(script);
        };

        loadGoogleMapsScript();

        return () => {
            const script = document.querySelector('script[src^="https://maps.googleapis.com/maps/api/js"]');
            if (script) script.remove();
        };
    }, []);

    const initAutocomplete = () => {
        const input = document.getElementById('address');
        const autocompleteInstance = new window.google.maps.places.AutocompleteService();

        setAutocomplete(autocompleteInstance);
    };
    const handleAddressChange = (e) => {
        const { value } = e.target;
        setInputs((prev) => ({ ...prev, address: value }));

        if (autocomplete) {
            autocomplete.getPlacePredictions({ input: value }, (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    setSuggestions(predictions);
                } else {
                    setSuggestions([]);
                }
            });
        } else {
            setSuggestions([]);
        }
    };
    const handleAddressSelect = async (placeId, description) => {
        const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

        placesService.getDetails({ placeId }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            setInputs((prev) => ({
                ...prev,
                address: description,
                country: getAddressComponent(place, 'country', 'long'),
                countryCode: getAddressComponent(place, 'country', 'short'), // ✅ used later
                state: getAddressComponent(place, 'administrative_area_level_1'),
                city: getAddressComponent(place, 'locality'),
                postalCode: getAddressComponent(place, 'postal_code'),
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                location: place.geometry.location.lat() + '° N, ' + place.geometry.location.lng() + '° E'
            }));

            setMarkers([{ geocode: [place.geometry.location.lat(), place.geometry.location.lng()] }]);
            }
        });

        setSuggestions([]);
    };
    const getAddressComponent = (place, type, nameType = 'long') => {
        const component = place.address_components?.find((comp) => comp.types.includes(type));
        if (!component) return '';
        return nameType === 'short' ? component.short_name : component.long_name;
    };
    const handleShowModal = (e) => {
        e.preventDefault();
        setShowCustomerFormModal();
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        const newValue =
            name === 'mobileNumber' || name === 'phoneNumber'
                ? value.replace(/\D/g, '').slice(0, 10)
                : name === 'postalCode' || name === 'vat'
                ? value.replace(/\D/g, '')
                : value;

        setInputs((prev) => ({
        ...prev,
            [name]: newValue,
        }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            inputs.contactName.length > 0 &&
            inputs.address.length > 0 &&
            inputs.city.length > 0 &&
            inputs.postalCode.length > 0 &&
            inputs.vat.length > 0
        ) {
            Swal.fire({
                title: `${t("are you sure")}?`,
                text: `${t(`you want to create new ${(type === "delivery address") ? type : "transportation company address"}`)}!`,
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: `${t("yes")}!`,
                cancelButtonText: `${t("cancel")}!`,
                width: "21rem",
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        setFormButton(true);

                        const baseUrl = env.REACT_APP_API_URL;
                        const token = localStorage.getItem("token");

                        // 🔹 Step 1: Prepare local payload for ERP
                        let payload = {
                            customerId: customerId,
                            contactname: inputs.contactName,
                            postalcode: inputs.postalCode,
                            floor: "",
                            address: inputs.address,
                            country: inputs.country,
                            state: inputs.state,
                            city: inputs.city,
                            mobile: inputs.mobileNumber,
                            phone: inputs.phoneNumber,
                            email: inputs.email,
                            vat: inputs.vat,
                            comments: inputs.comments,
                            latgps: inputs.latitude,
                            longgps: inputs.longitude,
                            type: type,
                        };

                        const headers = {
                            Accept: "Application/json",
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        };
                        if (type === "delivery address") {
                            const REACT_APP_CLIENT_API_URL = env.REACT_APP_RPC_URL;
                            const tenantId = "";
                            const clientId = "";
                            const clientSecret = "";
                            const environment = "";
                            const companyId = "";

                            // 🔹 Step 2: Fetch currency code dynamically using country code
                            let currencyCode = "EUR";
                            if (inputs.countryCode) {
                                try {
                                    const res = await fetch(`https://restcountries.com/v3.1/alpha/${inputs.countryCode}`);
                                    const data = await res.json();
                                    const currencies = data[0]?.currencies;
                                    currencyCode = currencies ? Object.keys(currencies)[0] : "EUR"; // default fallback
                                } catch (err) {
                                    console.warn("Failed to fetch currency, fallback to EUR");
                                    currencyCode = "EUR";
                                }
                            }

                            // 🔹 Step 3: Call API to create address in Business Central
                            const clientToken = localStorage.getItem('clientToken');
                            if (!clientToken) throw new Error("Client token not found");

                            const bcResponse = await axios.post(`${REACT_APP_CLIENT_API_URL}/rpc/bc_create_customer_client_credentials`,
                                {
                                    p_tenant_id: tenantId,
                                    p_client_id: clientId,
                                    p_client_secret: clientSecret,
                                    p_environment: environment,
                                    p_company_id: companyId,
                                    customerid_: customerId,
                                    p_payload: {
                                        city: inputs.city,
                                        type: "Person",
                                        email: inputs.email,
                                        state: inputs.state,
                                        country: inputs.countryCode,
                                        postalCode: inputs.postalCode,
                                        displayName: inputs.contactName,
                                        phoneNumber: inputs.mobileNumber,
                                        phone: inputs.phoneNumber,
                                        addressLine1: inputs.address,
                                        addressLine2: inputs.floor || "",
                                        currencyCode: currencyCode,
                                        vat: inputs.vat,
                                        comments: inputs.comments,
                                        latgps: inputs.latitude,
                                        longgps: inputs.longitude
                                    },
                                },
                                {
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${clientToken}`,
                                    },
                                }
                            );
                            if (bcResponse.data?.success === false) {
                                Swal.fire({
                                    title: `${t("oops")}!`,
                                    text: bcResponse.data?.message ? t(bcResponse.data.message.toLowerCase()) : `${t("something went wrong")}!`,
                                });
                                return;
                            }
                            if (bcResponse.data?.existing_address) {
                                // address already linked to another customer in BC — reuse it, don't create a new BC customer
                                payload.personcode = bcResponse.data.personcode;
                                payload.personlookupcode = bcResponse.data.personlookupcode;
                                payload.existingaddress = true;
                            } else {
                                const bcCustomerId = bcResponse.data?.number;
                                payload.personcode = bcCustomerId;
                            }
                        }

                        // 🔹 Step 5: Save final delivery address to ERP database
                        const response = await axios.post(
                            `${baseUrl}/api/Customer/CreateCustomerDeliveryAddress`,
                            payload,
                            { headers }
                        );

                        if (response.data.success === "1") {
                            sendInfoFromChild("yes");
                            Swal.fire({
                                title: `${t("created")}!`,
                                text: `${t("new address created")}!`,
                                icon: "success",
                            });
                        } else {
                            sendInfoFromChild("no");
                        }
                    } catch (error) {
                        console.error("Error in address submission:", error);
                        if (error.response && (error.response.status === 401 || error.response.status === 404)) {
                            localStorage.removeItem("token");
                            localStorage.removeItem("userInfo");
                            window.location.reload();
                        } else {
                            Swal.fire({
                                title: `${t("oops")}!`,
                                text: `${t("something went wrong")}!`,
                                icon: "warning",
                            });
                        }
                    } finally {
                        setFormButton(false);
                    }
                }
            });
        }
    };

    return (
        <div>
            <div className="fixed z-50 overflow-y-auto top-0 w-full left-0 h-full">
                <div className="flex items-center justify-center min-height-100vh text-center sm:block sm:p-0 h-full">
                    <div className="fixed inset-0 transition-opacity">
                        <div className="absolute inset-0 bg-gray-900 opacity-75" />
                    </div>
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                    <div
                        className="inline-block align-center bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-[26rem] w-full"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="modal-headline"
                    >
                        <div className="bg-white p-2 sm:p-4 space-y-1 sm:space-y-3">
                            <div className="flex justify-between items-center space-x-4 sm:space-x-0">
                                <div className="font-semibold text-sm sm:text-base capitalize">
                                    { t(title)}
                                </div>
                                <div className="cursor-pointer" onClick={(e) => (!formButton && handleShowModal(e))}>
                                    <img src="../../../assets/icons/modal-cross.svg" alt="cross" />
                                </div>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="space-y-2">
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="col-span-2 text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('contact name')}<span className='text-red-normal'>*</span></h1>
                                            <input
                                                type="text"
                                                name="contactName"
                                                value={inputs.contactName}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('enter contact name')}
                                            />
                                        </div>
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('postal code')}<span className='text-red-normal'>*</span></h1>
                                            <input
                                                type="text"
                                                name="postalCode"
                                                value={inputs.postalCode}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('ex: 23442')}
                                            />
                                        </div>
                                    </div>
                                    <div className="relative text-sm space-y-1">
                                        <h1 className="font-semibold capitalize">{t('address')}<span className='text-red-normal'>*</span></h1>
                                        <input
                                            type="text"
                                            id="address"
                                            name="address"
                                            value={inputs.address}
                                            onChange={handleAddressChange}
                                            className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                            placeholder={t('enter full address details')}
                                        />
                                        {suggestions.length > 0 && (
                                            <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg w-full mt-1">
                                                {suggestions.map((suggestion) => (
                                                <li
                                                    key={suggestion.place_id}
                                                    onClick={() => handleAddressSelect(suggestion.place_id, suggestion.description)}
                                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                                >
                                                    {suggestion.description}
                                                </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('country')}</h1>
                                            <input
                                                type="text"
                                                name="country"
                                                value={inputs.country}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('enter country')}
                                            />
                                        </div>
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('state')}</h1>
                                            <input
                                                type="text"
                                                name="state"
                                                value={inputs.state}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('enter state')}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('city')}<span className='text-red-normal'>*</span></h1>
                                            <input
                                                type="text"
                                                name="city"
                                                value={inputs.city}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('enter city')}
                                            />
                                        </div>
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('view on map')}</h1>
                                            <div className="border-2 border-blue-light2 bg-blue-light2 rounded-lg py-2 px-3 flex items-center space-x-2 text-sm">
                                                <input
                                                    type="text"
                                                    name="map"
                                                    value={inputs.location}
                                                    className="focus:outline-none bg-blue-light2 w-full placeholder:capitalize"
                                                    placeholder={t('--')}
                                                    disabled
                                                />
                                                <img
                                                    onClick={(e) => ((inputs.latitude && inputs.longitude) && setShowMap(!showMap))}
                                                    className={(inputs.latitude && inputs.longitude) ? "cursor-pointer" : "cursor-not-allowed"}
                                                    src="../../assets/icons/profile/location.svg"
                                                    alt="location"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-sm space-y-1">
                                        <h1 className="font-semibold capitalize">{t('email address')}</h1>
                                        <input
                                            type="email"
                                            name="email"
                                            value={inputs.email}
                                            onChange={handleChange}
                                            className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                            placeholder={t('enter email address')}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('mobile number')}</h1>
                                            <input
                                                type="text"
                                                name="mobileNumber"
                                                value={inputs.mobileNumber}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('ex: 8887 777799')}
                                            />
                                        </div>
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('phone number')}</h1>
                                            <input
                                                type="text"
                                                name="phoneNumber"
                                                value={inputs.phoneNumber}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('ex: 8887 777799')}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        <div className="text-sm space-y-1">
                                            <h1 className="font-semibold">{formatVatLabel(t('vat number'))}<span className='text-red-normal'>*</span></h1>
                                            <input
                                                type="text"
                                                name="vat"
                                                value={inputs.vat}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={t('ex 22223334')}
                                            />
                                        </div>
                                        <div className="sm:col-span-2 text-sm space-y-1">
                                            <h1 className="font-semibold capitalize">{t('comments')}</h1>
                                            <input
                                                type="text"
                                                name="comments"
                                                value={inputs.comments}
                                                onChange={handleChange}
                                                className="border-background-normal focus:outline-none bg-white border-2 rounded-lg px-3 py-2 w-full placeholder:capitalize"
                                                placeholder={ t("write comments here...")}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end sm:pt-2 items-center space-x-2">
                                        <button onClick={(e) => (!formButton && handleShowModal(e))} type="button" className="text-center px-4 text-xs font-medium sm:text-base py-2 text-blue-normal bg-white border border-blue-normal rounded-lg tracking-wide first-letter:capitalize">
                                            {t('cancel')}
                                        </button>
                                        { !formButton 
                                            ?
                                                <button type={(inputs.contactName.length > 0 && inputs.address.length > 0 && inputs.city.length > 0 && inputs.postalCode.length > 0 && inputs.vat.length > 0) ? "submit" : "button"} className={((inputs.contactName.length > 0 && inputs.address.length > 0 && inputs.city.length > 0 && inputs.postalCode.length > 0 && inputs.vat.length > 0) ? "cursor-pointer bg-blue-normal border-blue-normal" : "cursor-not-allowed bg-blue-disable border-blue-disable") + " text-center px-2 lg:px-4 text-sm font-medium lg:text-base py-2 text-white border rounded-lg tracking-wide capitalize"}>{ t('add address')}</button>
                                            :
                                                <button type="button" className="cursor-wait bg-blue-normal border-blue-normal capitalize text-center px-2 lg:px-4 text-sm font-medium lg:text-base py-2 text-white border rounded-lg tracking-wide ">{ t('Please wait, in-progress') }</button>
                                        }
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {/* Map Popup */}
            {showMap && (
                <div className="overflow-y-auto overflow-x-hidden bg-blue-300 bg-opacity-50 fixed flex flex-col top-0 right-0 left-0 z-50 justify-center items-center w-full inset-0 max-h-full">
                    <div className="relative p-4 w-full max-w-5xl max-h-full bg-white rounded-lg">
                        <button onClick={(e) => setShowMap(!showMap)} className="absolute right-1 top-1 border border-gray-400 shadow p-3 bg-white z-20 text-gray-400 rounded-full" >
                            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                            </svg>    
                        </button>
                        <MapComponent markers={markers} draggable={false} radius={0}/>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerFormModal;
